// Wholesale Order Form (B2B quick order grid)
// Quantities on /pages/wholesale are validated against the catalog's quantity
// rules (min / increment / max, exposed as data attributes by the template),
// then posted to the THEME cart and sent straight to checkout. The theme cart
// carries the signed-in company context, so catalog pricing + B2B checkout
// come along for free. The cart drawer is never opened.
//
// Loaded from templates/page.wholesale.liquid (not scripts.liquid), so it runs
// before scripts.base.js — hence plain querySelector instead of $1/$$.
(function () {
    const form = document.querySelector('.wholesale-order');
    if (!form) return;

    // -- Signed-out, just applied (?applied=1 from the Forms redirect): swap the intro --
    if (new URLSearchParams(window.location.search).get('applied') === '1') {
        const def = form.querySelector('.intro--default');
        const app = form.querySelector('.intro--applied');
        if (def && app) { def.hidden = true; app.hidden = false; }
        // They just applied — nothing to do until the team approves, so hide both
        // buttons and the collapsed form.
        const actionsEl = form.querySelector('.signed-out-body .actions');
        const revealEl  = form.querySelector('.signup-reveal');
        if (actionsEl) actionsEl.hidden = true;
        if (revealEl)  revealEl.hidden = true;
    }

    // -- Signed-out: Sign In shows the loader while the account portal loads --
    const signinBtn = form.querySelector('.btn.wholesale-signin');
    if (signinBtn) {
        signinBtn.addEventListener('click', () => signinBtn.classList.add('loading'));
        // Back/forward cache can restore the page with the class still on.
        window.addEventListener('pageshow', () => signinBtn.classList.remove('loading'));
    }

    // -- Signed-out: "Apply for an Account" slides the request form open --
    const applyBtn = form.querySelector('.btn.wholesale-apply');
    const reveal   = form.querySelector('.signup-reveal');
    if (applyBtn && reveal) {
        // While the Forms submission is in flight (until its redirect), show the
        // loader on the Apply button. Forms renders inside its own tree, so listen
        // for the submit click via composedPath rather than a submit event, and
        // clear the state after a timeout in case validation stopped the submit.
        let submitTimer = null;
        reveal.addEventListener('click', (e) => {
            const path = e.composedPath ? e.composedPath() : [e.target];
            const isSubmit = path.some(el => el && el.tagName === 'BUTTON' && (el.type === 'submit' || !el.type));
            if (!isSubmit) return;
            applyBtn.classList.add('loading');
            clearTimeout(submitTimer);
            submitTimer = setTimeout(() => applyBtn.classList.remove('loading'), 8000);
        }, true);
        window.addEventListener('pageshow', () => applyBtn.classList.remove('loading'));

        applyBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const open = !reveal.classList.contains('open');
            reveal.classList.toggle('open', open);
            applyBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
            if (open) {
                // Let the reveal start, then bring the form into view + focus its first field.
                setTimeout(() => {
                    reveal.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    const first = reveal.querySelector('input, select, textarea');
                    if (first) setTimeout(() => first.focus({ preventScroll: true }), 300);
                }, 60);
            }
        });
    }

    const cards       = Array.from(form.querySelectorAll('.product-card'));
    const cells       = Array.from(form.querySelectorAll('.cell[data-variant-id]'));
    const subtotalEl  = form.querySelector('[data-subtotal]');
    const checkoutBtn = form.querySelector('.btn.wholesale-checkout');
    const errorEl     = form.querySelector('.order-error');
    const config      = document.querySelector('#wholesale-data');
    if (cells.length === 0) return;

    // -- Helpers --

    function priceString(cents) {
        const dollars = Math.floor(cents / 100);
        const rest = String(cents % 100).padStart(2, '0');
        return `$${dollars.toLocaleString()}.${rest}`;
    }

    function readQty(input) {
        const n = parseInt(input.value, 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
    }

    function rules(cell) {
        return {
            increment: parseInt(cell.dataset.increment, 10) || 1,
            max:       cell.dataset.max ? parseInt(cell.dataset.max, 10) : null,
        };
    }

    // Per-row checks. Returns a short problem string, or null when fine.
    // `label` is optional context for the full sentence shown in the error bar.
    function validateRow(cell, qty, label) {
        if (qty === 0) return null;
        const { increment, max } = rules(cell);
        const what = label ? ` ${label}` : '';
        if (max !== null && qty > max) return `Maximum ${max}${what}.`;
        if (increment > 1 && qty % increment !== 0) return `${label || 'This item'} must be ordered in multiples of ${increment}.`;
        return null;
    }

    // Card-level minimum: total across all flavors on the card (data-min on the
    // card). Zero is fine (nothing ordered); anything above zero must reach it.
    function cardMin(card) {
        return parseInt(card.dataset.min, 10) || 1;
    }
    function validateCard(card, cardQty) {
        if (cardQty === 0) return null;
        const min = cardMin(card);
        if (cardQty < min) return `Minimum ${min} ${card.dataset.cardTitle || 'items'} required.`;
        return null;
    }

    // -- Totals --

    function updateTotals() {
        let orderTotal = 0;

        cards.forEach(card => {
            let cardTotal = 0;
            let cardQty = 0;
            card.querySelectorAll('.cell[data-variant-id]').forEach(cell => {
                const input = cell.querySelector('input');
                const lineEl = cell.querySelector('.line-total');
                if (!input || input.disabled) return;

                const qty = readQty(input);
                const problem = validateRow(cell, qty);
                const line = qty * parseInt(cell.dataset.price, 10);

                cell.classList.toggle('invalid', problem !== null);
                cell.classList.toggle('has-qty', qty > 0);
                // No per-flavor totals; the slot only surfaces a row problem (increment / max).
                if (lineEl) lineEl.textContent = problem ? problem.replace(/\.$/, '') : '';

                if (!problem) {
                    cardTotal += line;
                    cardQty += qty;
                }
            });

            // Combined minimum across the card's flavors. The subtotal still shows;
            // the card + its "Min Qty" text get highlighted via .invalid instead.
            const cardProblem = validateCard(card, cardQty);
            card.classList.toggle('invalid', cardProblem !== null);
            const cardEl = card.querySelector('[data-card-subtotal]');
            if (cardEl) cardEl.textContent = priceString(cardTotal);
            orderTotal += cardTotal;
        });

        if (subtotalEl) subtotalEl.textContent = priceString(orderTotal);
        return orderTotal;
    }

    cells.forEach(cell => {
        const input = cell.querySelector('input');
        if (!input) return;
        input.addEventListener('input', () => { showError(''); updateTotals(); });
        input.addEventListener('blur', () => {
            input.value = readQty(input);   // tidy blanks / non-integers
            updateTotals();
        });
        input.addEventListener('focus', () => input.select());
    });

    // -- Submit --

    function showError(message) {
        if (!errorEl) return;
        errorEl.textContent = message || '';
        errorEl.hidden = !message;
    }

    // Bring the first offending card into view (centered, so the sticky header
    // and footer don't cover it) and focus its first quantity field.
    function scrollToFirstProblem() {
        const target = form.querySelector('.product-card.invalid, .cell.invalid');
        if (!target) return;
        const card = target.closest('.product-card') || target;
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = card.querySelector('.cell.invalid input, .cell input:not(:disabled)');
        if (input) setTimeout(() => input.focus({ preventScroll: true }), 400);
    }

    function setLoading(isLoading) {
        if (!checkoutBtn) return;
        checkoutBtn.classList.toggle('loading', isLoading);
        checkoutBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    function collectItems() {
        const items = [];
        const problems = [];
        cards.forEach(card => {
            let cardQty = 0;
            const cardItems = [];
            card.querySelectorAll('.cell[data-variant-id]').forEach(cell => {
                const input = cell.querySelector('input');
                if (!input || input.disabled) return;
                const qty = readQty(input);
                if (qty === 0) return;
                const problem = validateRow(cell, qty, cell.dataset.title);
                if (problem) {
                    problems.push(problem);
                } else {
                    cardItems.push({ id: parseInt(cell.dataset.variantId, 10), quantity: qty });
                    cardQty += qty;
                }
            });
            const cardProblem = validateCard(card, cardQty);
            if (cardProblem) problems.push(cardProblem);
            else items.push(...cardItems);
        });
        return { items, problems };
    }

    async function postJSON(url, body) {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(body || {}),
        });
        let data = null;
        try { data = await res.json(); } catch (e) { /* some endpoints return empty */ }
        if (!res.ok) {
            const msg = data?.description || data?.message || `Request failed (${res.status})`;
            throw new Error(msg);
        }
        return data;
    }

    let submitting = false;
    async function submit() {
        if (submitting) return;
        showError('');

        const { items, problems } = collectItems();
        if (problems.length) {
            showError(problems[0]);   // first problem only; highlighted cards show the rest
            scrollToFirstProblem();
            return;
        }
        if (items.length === 0) {
            showError('Enter a quantity for at least one item.');
            return;
        }

        submitting = true;
        setLoading(true);

        try {
            // The form is the source of truth: start from an empty cart so stale
            // lines (or retail items from before this account was B2B) don't ride along.
            await postJSON('/cart/clear.js');
            await postJSON('/cart/add.js', { items });
            // Flag the cart so wholesale orders are easy to separate in analytics.
            await postJSON('/cart/update.js', { attributes: { wholesale: 'true' } });

            if (window.posthog) {
                try {
                    posthog.capture('wholesale_checkout_started', {
                        company:    config?.dataset.company || null,
                        line_count: items.length,
                        unit_count: items.reduce((n, i) => n + i.quantity, 0),
                        subtotal:   updateTotals() / 100,
                        currency:   'USD',
                    });
                } catch (e) { /* never block checkout */ }
            }

            window.location.href = '/checkout';
        } catch (err) {
            console.error('Wholesale checkout failed:', err);
            showError(err.message || 'Something went wrong building your order. Please try again.');
            setLoading(false);
            submitting = false;
        }
    }

    if (checkoutBtn) {
        checkoutBtn.addEventListener('click', (e) => {
            e.preventDefault();
            submit();
        });
    }
    form.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.matches('input')) {
            e.preventDefault();
            submit();
        }
    });

    updateTotals();
}());
